import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../config/supabase';
import './CustomerResetPassword.css';

const CustomerResetPassword = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [validToken, setValidToken] = useState(false);
  
  const navigate = useNavigate();

  useEffect(() => {
    // Check if we have a valid reset token
    const checkToken = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        setValidToken(true);
      } else {
        setError('Invalid or expired reset link. Please request a new one.');
      }
    };

    checkToken();
  }, []);

  const validatePassword = (password) => {
    if (password.length < 6) {
      return 'Password must be at least 6 characters long';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    // Validate password
    const passwordError = validatePassword(password);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    // Check if passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: password
      });

      if (updateError) {
        throw updateError;
      }

      setSuccess(true);
      
      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate('/login');
      }, 2000);
      
    } catch (err) {
      setError(err.message || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!validToken && error) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <div className="auth-logo">
              <img 
                src="https://akuoggticrcsctgkerfl.supabase.co/storage/v1/object/public/public-images/Screenshot_2025-11-20_005401__1_-removebg-preview.png" 
                alt="Company logo" 
                style={{ width: '180px', height: 'auto', borderRadius: '16px' }} 
              />
            </div>
            <h1 className="auth-title">Reset Password</h1>
          </div>

          <div className="auth-error">
            <span className="error-icon">❌</span>
            {error}
          </div>

          <div className="auth-footer">
            <Link to="/forgot-password" className="auth-button" style={{ textAlign: 'center', display: 'block' }}>
              Request New Reset Link
            </Link>
            <p style={{ marginTop: '15px' }}>
              <Link to="/login" className="auth-link">Back to Sign In</Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <div className="auth-logo">
            <img 
              src="https://akuoggticrcsctgkerfl.supabase.co/storage/v1/object/public/public-images/Screenshot_2025-11-20_005401__1_-removebg-preview.png" 
              alt="Company logo" 
              style={{ width: '180px', height: 'auto', borderRadius: '16px' }} 
            />
          </div>
          <h1 className="auth-title">Reset Password</h1>
          <p className="auth-subtitle">Enter your new password</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {error && (
            <div className="auth-error">
              <span className="error-icon">❌</span>
              {error}
            </div>
          )}

          {success && (
            <div className="auth-success">
              <span className="success-icon">✅</span>
              Password reset successful! Redirecting to login...
            </div>
          )}

          <div className="form-group">
            <label htmlFor="password">New Password</label>
            <input
              type="password"
              id="password"
              name="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter new password"
              required
              disabled={loading || success}
              minLength={6}
            />
            <small className="form-hint">Password must be at least 6 characters</small>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              required
              disabled={loading || success}
              minLength={6}
            />
          </div>

          <button 
            type="submit" 
            className="auth-button"
            disabled={loading || success}
          >
            {loading ? 'Resetting...' : success ? 'Success!' : 'Reset Password'}
          </button>
        </form>

        <div className="auth-footer">
          <p>
            <Link to="/login" className="auth-link">Back to Sign In</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default CustomerResetPassword;

